import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Award, Heart, MessageCircle, BookOpen, Calendar, Users, Star, Flame, Target, Crown, Zap } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_BADGES = [
  { id: 'early-member', name: 'Early Member', desc: 'Joined the community in its first year', icon: Star, color: '#D4AF37', requirement: 'Join the community' },
  { id: 'first-post', name: 'First Post', desc: 'Published your first community post', icon: Zap, color: '#C25E3E', requirement: 'Create 1 post' },
  { id: 'storyteller', name: 'Storyteller', desc: 'Published 5 community posts', icon: BookOpen, color: '#4A6FA5', requirement: 'Create 5 posts' },
  { id: 'voice', name: 'The Voice', desc: 'Published 10 community posts', icon: MessageCircle, color: '#7A5BA5', requirement: 'Create 10 posts' },
  { id: 'liked-10', name: 'Appreciated', desc: 'Received 10 likes on your posts', icon: Heart, color: '#E84393', requirement: 'Receive 10 likes' },
  { id: 'liked-50', name: 'Beloved', desc: 'Received 50 likes on your posts', icon: Heart, color: '#C25E3E', requirement: 'Receive 50 likes' },
  { id: 'commenter', name: 'Connector', desc: 'Left 10 comments on others\' posts', icon: MessageCircle, color: '#2E8B57', requirement: 'Comment 10 times' },
  { id: 'course-1', name: 'Learner', desc: 'Completed your first course', icon: BookOpen, color: '#1A8A7D', requirement: 'Complete 1 course' },
  { id: 'course-5', name: 'Scholar', desc: 'Completed 5 courses', icon: Award, color: '#4A6FA5', requirement: 'Complete 5 courses' },
  { id: 'event-1', name: 'Attendee', desc: 'RSVPd to your first event', icon: Calendar, color: '#C4792E', requirement: 'RSVP to 1 event' },
  { id: 'mentor', name: 'Mentor', desc: 'Completed a mentorship programme', icon: Users, color: '#7A5BA5', requirement: 'Complete mentorship' },
  { id: 'nominator', name: 'Nominator', desc: 'Nominated a woman for the platform', icon: Target, color: '#C25E3E', requirement: 'Submit a nomination' },
  { id: 'streak-7', name: 'On Fire', desc: '7-day activity streak', icon: Flame, color: '#E84393', requirement: '7 consecutive active days' },
  { id: 'influencer', name: 'Influencer', desc: 'Received 100+ total engagement', icon: Crown, color: '#D4AF37', requirement: '100+ likes + comments received' },
  { id: 'founder', name: 'Founder', desc: 'Platform founder badge', icon: Crown, color: '#C25E3E', requirement: 'Founding team only' },
];

const BadgeIcon = ({ badge, earned = false, size = 'normal' }) => {
  const Icon = badge.icon;
  const s = size === 'large' ? 'w-20 h-20' : size === 'small' ? 'w-10 h-10' : 'w-14 h-14';
  const iconSize = size === 'large' ? 32 : size === 'small' ? 16 : 22;
  return (
    <div className={`${s} rounded-full flex items-center justify-center transition-all ${earned ? 'shadow-lg' : 'opacity-30 grayscale'}`} style={{ background: earned ? `${badge.color}15` : '#F3EFE9', border: `2px solid ${earned ? badge.color : '#E0DDD6'}` }}>
      <Icon size={iconSize} style={{ color: earned ? badge.color : '#C0BBB2' }} />
    </div>
  );
};

const Initials = ({ name, size = 'w-10 h-10 text-sm' }) => {
  const init = name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  return <div className={`${size} rounded-full bg-[#C25E3E] text-white flex items-center justify-center flex-shrink-0`} style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>{init}</div>;
};

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('leaderboard');

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/community/leaderboard`);
        setLeaders(res.data.leaders || []);
      } catch { } finally { setLoading(false); }
    };
    fetchLeaderboard();
  }, []);

  const myBadges = user?.badges || ['Early Member'];

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-32 pb-20" data-testid="leaderboard-page">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-12">
        <p className="text-[12px] tracking-[0.3em] uppercase mb-4" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>COMMUNITY</p>
        <h1 className="text-4xl lg:text-[64px] leading-[0.95] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>
          Leaderboard & <span className="text-[#C25E3E]">Badges</span>
        </h1>
        <p className="text-lg max-w-xl" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>
          Celebrate the most active community members. Earn badges by contributing, learning, and connecting.
        </p>
      </section>

      {/* Tabs */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-12">
        <div className="flex gap-1 mb-10 border-b border-[#1A1A1A]/10">
          {[{ id: 'leaderboard', label: 'Leaderboard' }, { id: 'badges', label: 'All Badges' }, ...(user ? [{ id: 'my-badges', label: 'My Badges' }] : [])].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-6 py-3 text-[12px] tracking-[0.12em] uppercase border-b-2 transition-all ${tab === t.id ? 'border-[#C25E3E] text-[#C25E3E]' : 'border-transparent text-[#888] hover:text-[#1A1A1A]'}`} style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }} data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* LEADERBOARD TAB */}
        {tab === 'leaderboard' && (
          <div>
            {/* Top 3 podium */}
            {leaders.length >= 3 && (
              <div className="grid grid-cols-3 gap-4 mb-12">
                {[leaders[1], leaders[0], leaders[2]].map((l, i) => {
                  const rank = i === 0 ? 2 : i === 1 ? 1 : 3;
                  const colors = { 1: '#D4AF37', 2: '#A8A8A8', 3: '#C4792E' };
                  const heights = { 1: 'pt-6', 2: 'pt-12', 3: 'pt-4' };
                  return (
                    <div key={l.id} className={`bg-white border border-[#1A1A1A]/8 p-6 text-center ${heights[rank]} transition-all hover:shadow-md`} data-testid={`podium-${rank}`}>
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center text-white text-lg font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", background: colors[rank] }}>
                        {rank}
                      </div>
                      <Initials name={l.name} size="w-16 h-16 text-xl mx-auto mb-3" />
                      <h3 className="text-lg mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: '#1A1A1A' }}>{l.name}</h3>
                      <p className="text-[11px] text-[#888] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{l.headline || 'Community Member'}</p>
                      <p className="text-2xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif", color: colors[rank] }}>{l.score}</p>
                      <p className="text-[9px] tracking-[0.15em] uppercase text-[#888]" style={{ fontFamily: "'Outfit', sans-serif" }}>POINTS</p>
                      <div className="flex justify-center gap-1 mt-3">
                        {(l.badges || []).slice(0, 4).map((b, bi) => {
                          const badge = ALL_BADGES.find(ab => ab.id === b || ab.name === b);
                          return badge ? <BadgeIcon key={bi} badge={badge} earned size="small" /> : null;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Full leaderboard */}
            <div className="space-y-2">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-16 bg-[#F3EFE9] animate-pulse" />)
              ) : leaders.length === 0 ? (
                <div className="text-center py-16 bg-white border border-[#1A1A1A]/8">
                  <Trophy size={40} className="mx-auto mb-4 text-[#1A1A1A]/20" />
                  <p className="text-lg" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A1A1A' }}>Be the first on the leaderboard!</p>
                  <p className="text-[13px] text-[#888] mt-2" style={{ fontFamily: "'Lora', serif" }}>Post, comment, and engage to earn points.</p>
                  <Link to="/community" className="inline-flex items-center mt-6 h-10 px-6 bg-[#C25E3E] text-white text-[11px] uppercase tracking-[0.15em] hover:bg-[#1A1A1A] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Go to Community</Link>
                </div>
              ) : (
                leaders.map((l, i) => (
                  <div key={l.id} className={`flex items-center gap-4 p-4 bg-white border transition-all hover:shadow-sm ${i < 3 ? 'border-[#D4AF37]/30' : 'border-[#1A1A1A]/8'}`} data-testid={`leader-${i}`}>
                    <span className="w-8 text-center text-lg font-light" style={{ fontFamily: "'Cormorant Garamond', serif", color: i < 3 ? '#D4AF37' : '#888' }}>
                      {i + 1}
                    </span>
                    <Initials name={l.name} size="w-10 h-10 text-sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-medium truncate" style={{ fontFamily: "'Outfit', sans-serif", color: '#1A1A1A' }}>{l.name}</p>
                      <p className="text-[11px] text-[#888] truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{l.headline || 'Community Member'}</p>
                    </div>
                    <div className="flex gap-1">
                      {(l.badges || []).slice(0, 3).map((b, bi) => {
                        const badge = ALL_BADGES.find(ab => ab.id === b || ab.name === b);
                        return badge ? <BadgeIcon key={bi} badge={badge} earned size="small" /> : null;
                      })}
                    </div>
                    <div className="text-right w-20">
                      <p className="text-lg font-light" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#C25E3E' }}>{l.score}</p>
                      <p className="text-[8px] tracking-wider uppercase text-[#888]" style={{ fontFamily: "'Outfit', sans-serif" }}>PTS</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Scoring */}
            <div className="mt-12 bg-[#F3EFE9] p-8">
              <h3 className="text-xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>How Points Work</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[{ action: 'Create a post', pts: '+10' }, { action: 'Receive a like', pts: '+2' }, { action: 'Comment on post', pts: '+5' }, { action: 'Receive a comment', pts: '+3' }].map(s => (
                  <div key={s.action} className="bg-white p-4 text-center">
                    <p className="text-2xl font-light mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#C25E3E' }}>{s.pts}</p>
                    <p className="text-[11px] text-[#4A4A4A]" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.action}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ALL BADGES TAB */}
        {tab === 'badges' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ALL_BADGES.map(badge => (
              <div key={badge.id} className="bg-white border border-[#1A1A1A]/8 p-6 flex items-start gap-4 transition-all hover:shadow-md" data-testid={`badge-${badge.id}`}>
                <BadgeIcon badge={badge} earned size="normal" />
                <div>
                  <h3 className="text-[16px] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: '#1A1A1A' }}>{badge.name}</h3>
                  <p className="text-[13px] mb-2" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>{badge.desc}</p>
                  <p className="text-[10px] tracking-wide uppercase" style={{ fontFamily: "'Outfit', sans-serif", color: badge.color, fontWeight: 600 }}>{badge.requirement}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* MY BADGES TAB */}
        {tab === 'my-badges' && user && (
          <div>
            <div className="bg-white border border-[#1A1A1A]/8 p-8 mb-8 text-center">
              <Initials name={user.name} size="w-20 h-20 text-2xl mx-auto mb-4" />
              <h2 className="text-2xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>{user.name}</h2>
              <p className="text-[13px] text-[#888] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{user.headline || 'Community Member'}</p>
              <p className="text-[12px] text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>{myBadges.length} of {ALL_BADGES.length} badges earned</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {ALL_BADGES.map(badge => {
                const earned = myBadges.includes(badge.name) || myBadges.includes(badge.id);
                return (
                  <div key={badge.id} className={`bg-white border p-6 flex items-start gap-4 transition-all ${earned ? 'border-[#1A1A1A]/8 hover:shadow-md' : 'border-dashed border-[#1A1A1A]/10 opacity-50'}`}>
                    <BadgeIcon badge={badge} earned={earned} size="normal" />
                    <div>
                      <h3 className="text-[16px] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: earned ? '#1A1A1A' : '#888' }}>{badge.name}</h3>
                      <p className="text-[13px]" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>{badge.desc}</p>
                      {!earned && <p className="text-[10px] mt-2 uppercase tracking-wide" style={{ fontFamily: "'Outfit', sans-serif", color: '#C25E3E' }}>{badge.requirement}</p>}
                      {earned && <p className="text-[10px] mt-2 uppercase tracking-wide text-[#2E8B57]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>EARNED</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default LeaderboardPage;
